import { DialogContent } from '@mui/material'
import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import _ from 'lodash'
import React, { useMemo, useState } from 'react'
import FlipMove from 'react-flip-move'
import KitIncludes from './KitIncludes'
import SaveAsDefaultKitToggle from './SaveAsDefaultKitToggle'
import VariantDetails from './VariantDetails'
import { ISupplyDataVariant, ISupplyFormData } from '@components/Product/VariantKitPanel/VariantKitPanel'
import { Formik } from 'formik'
import * as yup from 'yup'
import { useTranslation } from 'react-i18next'
import Divider from '@mui/material/Divider'

const schema = yup.object().shape({
  supplyLine: yup.number().nullable(),
  setAsDefault: yup.boolean(),
  variants: yup
    .array()
    .of(
      yup.object().shape({
        pk: yup.number().required(),
        supplyProductPk: yup.number().required(),
        quantity: yup.number().required(),
      })
    )
    .min(1),
})

interface FormData {
  supplyLine: number | null
  setAsDefault: boolean
  variants: { pk: number; quantity: number; supplyProductPk: number }[]
}

const getInitialSupplyProducts = (supplyProducts: ISupplyFormData['supplyData'], editMode = false) => {
  if (!editMode)
    return _.sortBy(
      [...supplyProducts],
      supplyProduct =>
        !supplyProduct.variants.find(v => v.availability || ['in', 'backorder'].includes(v.outOfStockStatus))
    )
  else {
    const isInCartProducts = supplyProducts.filter(({ variants }) => variants.find(variant => variant.isInCart))
    const notIsInCart = supplyProducts.filter(({ variants }) => !variants.find(variant => variant.isInCart))
    return [...isInCartProducts, ...notIsInCart]
  }
}

const findSupplyVariantById = (supplyProducts: ISupplyFormData['supplyData'], id: number): ISupplyDataVariant => {
  let v: ISupplyDataVariant

  supplyProducts.forEach(({ variants }) =>
    variants.forEach(variant => {
      if (variant.supplyVariantPk === id) {
        v = variant
      }
    })
  )
  return v
}

interface IProps {
  data: ISupplyFormData

  onSubmit(values: FormData): Promise<void>

  onClosePanel(): void

  isWholesale: boolean
}

const VariantKitForm: React.FC<IProps> = ({ data, onSubmit, onClosePanel, isWholesale }) => {
  const { t } = useTranslation()
  const [supplyProducts, setSupplyProducts] = useState(getInitialSupplyProducts(data.supplyData, !!data.cartLinePk))

  const findVariantIndex = (supplyProductPk: number) => {
    return _.findIndex(supplyProducts, ({ variants }) => {
      const i = _.findIndex(variants, variant => variant.supplyProductPk === supplyProductPk)
      return i > -1
    })
  }

  const moveToBottom = (supplyProductPk: number) => {
    const index = findVariantIndex(supplyProductPk)
    let res = supplyProducts.slice()
    if (index > -1 && index !== res.length - 1) {
      res = [...res.slice(0, index), ...res.slice(index + 1), res[index]]
    }
    setSupplyProducts(res)
  }
  const moveTop = (supplyProductPk: number) => {
    const index = findVariantIndex(supplyProductPk)
    let res = supplyProducts.slice()
    if (index > 0) {
      res = [res[index], ...res.slice(0, index), ...res.slice(index + 1)]
    }
    setSupplyProducts(res)
  }

  const initialValues: FormData = useMemo(() => {
    const values: FormData = {
      supplyLine: data.cartLinePk,
      setAsDefault: data.setAsDefault ?? false,
      variants: [],
    }
    data.supplyData.forEach(({ variants }) => {
      const initialVariant = variants.find(el => el.isInCart) || variants[0]
      const quantity = initialVariant.isInCart
        ? initialVariant.cartQuantity
        : !initialVariant.cartLine &&
          (initialVariant.stockQty || ['in', 'backorder'].includes(initialVariant.outOfStockStatus))
        ? 1
        : 0
      if (quantity > 0) {
        values.variants.push({
          pk: initialVariant.supplyVariantPk,
          supplyProductPk: initialVariant.supplyProductPk,
          quantity: quantity,
        })
      }
    })
    return values
  }, [JSON.stringify(data)])

  return (
    <Formik<FormData>
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={values => {
        const ids = data.supplyData.map(({ variants }) => variants.map(variant => variant.supplyVariantPk)).flat()
        const sortedVariants = _.sortBy(values.variants, variant => ids.indexOf(variant.pk))
        onSubmit({ ...values, variants: sortedVariants })
      }}
      enableReinitialize
      validateOnMount
    >
      {form => {
        const removeVariant = (supplyVariantPk: number) => {
          form.setFieldValue(
            'variants',
            [...form.values.variants].filter(o => o.pk !== supplyVariantPk)
          )
        }
        const addVariant = (supplyVariantPk: number, data: FormData['variants'][0]) => {
          const res = [...form.values.variants].filter(o => o.pk !== supplyVariantPk)
          res.push(data)
          form.setFieldValue('variants', res)
        }
        const itemsSelectedPrice = form.values.variants.reduce((prev, curr) => {
          const v = findSupplyVariantById(supplyProducts, curr.pk)
          return prev + curr.quantity * Number(isWholesale ? v?.wholesalePrice.gross || 0 : v?.price.gross || 0)
        }, 0)
        const kitIncludesItems = form.values.variants
          .filter(variant => variant.quantity > 0)
          .map(variant => {
            const v = findSupplyVariantById(supplyProducts, variant.pk)
            return {
              pk: variant.pk,
              quantity: variant.quantity,
              name:
                supplyProducts.find(({ variants }) => variants.some(o => o.supplyVariantPk === variant.pk))
                  ?.supplyProduct.specificsName || '',
              price: variant.quantity * Number(isWholesale ? v?.wholesalePrice.gross || 0 : v?.price.gross || 0),
            }
          })

        const currency = supplyProducts[0]?.supplyProduct?.currency || 'USD'
        return (
          <>
            <DialogContent
              className='w-full max-w-full'
              sx={{ padding: 0, display: 'flex', flexDirection: 'column', overflowY: 'hidden' }}
            >
              <div className='flex-shrink-0 px-10 sm:px-20 py-15'>
                <div className='text-base'>
                  {t('description.chooseWhatToIncludeInKit', 'Choose what to include in your kit')}
                </div>
              </div>
              <div className='flex-1 min-h-0 overflow-y-auto px-10 sm:px-20 pb-20'>
                <FlipMove enterAnimation='fade' leaveAnimation='fade' className='flex flex-col gap-10'>
                  {supplyProducts.map(({ supplyProduct, variants }) => {
                    return (
                      <div key={supplyProduct.id}>
                        <VariantDetails
                          variants={variants}
                          supplyProduct={supplyProduct}
                          onClosePanel={onClosePanel}
                          removeVariant={removeVariant}
                          addVariant={addVariant}
                          moveToBottom={moveToBottom}
                          moveTop={moveTop}
                          isWholesale={isWholesale}
                        />
                      </div>
                    )
                  })}
                </FlipMove>
              </div>
              <div className='my-20 px-10 sm:px-20'>
                <Divider />
              </div>
              <div className='flex-shrink-0 px-10 sm:px-20 pb-10'>
                <KitIncludes items={kitIncludesItems} totalPrice={itemsSelectedPrice} currency={currency} />
              </div>
            </DialogContent>
            <DialogActions className='flex flex-col items-stretch' style={{ padding: '10px 20px' }}>
              <div className='w-full mb-20 px-10'>
                <SaveAsDefaultKitToggle
                  checked={form.values.setAsDefault}
                  onChange={checked => form.setFieldValue('setAsDefault', checked)}
                />
              </div>
              <div className='flex flex-wrap items-center w-full gap-10 pb-10' style={{ margin: 0 }}>
                <span>
                  <Button variant='outlined' color='secondary' size='large' onClick={onClosePanel} fullWidth>
                    <span className='px-0 sm:px-30 text-base sm:text-lg'>{t('button.cancel', 'Cancel')}</span>
                  </Button>
                </span>
                <span className='flex-1'>
                  <Button
                    type='submit'
                    disabled={!form.isValid}
                    variant='contained'
                    color='secondary'
                    size='large'
                    onClick={() => form.handleSubmit()}
                    fullWidth
                  >
                    <span className='text-base sm:text-lg'>
                      {!form.isValid
                        ? t('button.chooseProduct', 'Choose product')
                        : !data.cartLinePk
                        ? t('button.addCustomizedKitToCart', 'Add Customized Kit to Cart')
                        : t('button.updateKit', 'Update the kit')}
                    </span>
                  </Button>
                </span>
              </div>
            </DialogActions>
          </>
        )
      }}
    </Formik>
  )
}

export default VariantKitForm
